import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http'
import { inject, InjectionToken } from '@angular/core'

import { catchError, switchMap, throwError, TimeoutError } from 'rxjs'

import { HttpRoutes } from '@enums/http-routes'
import { LogFilterWord } from '@enums/log-filter-word'
import { RecaptchaCall, RecaptchaError, RecaptchaFailureCause } from '@enums/recaptcha'
import { RecaptchaScriptMissingError, RecaptchaService } from '@services/recaptcha'
import { TransitionLogService } from '@services/transition-log-service'

/** The status Angular gives a request that never got a response; here, one that was never sent. */
const NOT_SENT_STATUS = 0

/**
 * How long a protected call waits for Google before giving up; 10 s follows Google's suggested timeout for its
 * mobile reCAPTCHA SDKs. Replaced by specs.
 */
export const RECAPTCHA_TIMEOUT_MS = new InjectionToken<number>('RECAPTCHA_TIMEOUT_MS', {
	providedIn: 'root',
	factory: () => 10_000,
})

/**
 * The call a request is when the gateway rejects it without a reCAPTCHA token; `undefined` when it needs none.
 * Mirrors the gateway's `recaptcha.protected-paths`: a path added or removed there must be added or removed here.
 */
const protectedCall = ({ url, method }: HttpRequest<unknown>): RecaptchaCall | undefined => {
	if (url.includes(HttpRoutes.FreedompayInit)) return RecaptchaCall.FreedompayInit
	if (url.includes(HttpRoutes.FreedompaySubmit)) return RecaptchaCall.FreedompaySubmit
	if (url.includes(HttpRoutes.PromoCodesGuestApplication)) {
		return method === 'DELETE' ? RecaptchaCall.PromoRemove : RecaptchaCall.PromoApply
	}

	return undefined
}

/** Why no token came, from what `refresh` failed with; anything other than a missing script or the limit is Google's. */
const failureCause = (error: unknown): RecaptchaFailureCause => {
	if (error instanceof RecaptchaScriptMissingError) return RecaptchaFailureCause.NoScript
	if (error instanceof TimeoutError) return RecaptchaFailureCause.Timeout

	return RecaptchaFailureCause.Rejected
}

/** What a protected call fails with when it was never sent for lack of a token. */
const recaptchaUnavailable = (url: string): HttpErrorResponse =>
	new HttpErrorResponse({
		url,
		status: NOT_SENT_STATUS,
		error: { code: RecaptchaError.Unavailable },
	})

/**
 * FreedomPay and guest promo code calls always carry a fresh reCAPTCHA token. Without one the call is never
 * sent, since the gateway would reject it: it fails as an HTTP error coded `RECAPTCHA_UNAVAILABLE` (status 0),
 * so the screen shows its error message instead of a spinner that never ends.
 *
 * Each call not sent leaves one Bugfender warning naming the call, the cause and how long it waited, since the
 * gateway never sees it. The line never carries the URL (a promo removal's holds the ticket) nor Google's error.
 */
export const recaptchaInterceptor: HttpInterceptorFn = (req, next) => {
	const call = protectedCall(req)

	if (!call) return next(req)

	const recaptchaService = inject(RecaptchaService)
	const transitionLog = inject(TransitionLogService)
	const timeoutMs = inject(RECAPTCHA_TIMEOUT_MS)
	const startedAt = performance.now()

	return recaptchaService.refresh(timeoutMs).pipe(
		catchError((error: unknown) => {
			transitionLog.warn(
				`reCAPTCHA unavailable · call ${call} · cause ${failureCause(error)} · after ${Math.round(performance.now() - startedAt)}ms · ${LogFilterWord.Recaptcha}`
			)

			return throwError(() => recaptchaUnavailable(req.url))
		}),
		switchMap((token) => {
			const headers = req.headers.set('ReCaptcha-Token', token)
			return next(req.clone({ headers }))
		})
	)
}
